import {
  AgUiEvent,
  RunErrorEvent,
  RunFinishedEvent,
  RunStartedEvent,
  TextMessageContentEvent,
  TextMessageEndEvent,
  TextMessageStartEvent,
  ToolCallArgsEvent,
  ToolCallEndEvent,
  ToolCallResultEvent,
  ToolCallStartEvent,
} from '../types/ag-ui.types';
import {
  ChatMessage,
  ChatToolCall,
  RunState,
  ToolCallStatus,
} from '../types/chat-message.type';

const PENDING_ID_PREFIX = 'pending-';

/**
 * Folds the AG-UI event stream of one `agent/run` into the message list.
 *
 * Kept a pure function so the whole protocol is testable without HTTP: text is
 * accumulated per messageId, tool arguments per toolCallId. Events the UI does
 * not render (REASONING_*, STEP_*, STATE_*, RAW, …) return the state unchanged,
 * by reference, so nothing downstream recomputes.
 */
export function reduceAgUiEvent(state: RunState, event: AgUiEvent): RunState {
  switch (event.type) {
    case 'RUN_STARTED':
      return {
        ...state,
        running: true,
        error: undefined,
        currentRunId: (event as RunStartedEvent).runId,
      };

    case 'TEXT_MESSAGE_START':
      return withMessage(state, (event as TextMessageStartEvent).messageId);

    case 'TEXT_MESSAGE_CONTENT': {
      const { messageId, delta } = event as TextMessageContentEvent;
      if (!delta) return state;
      return mapMessage(withMessage(state, messageId), messageId, (m) => ({
        ...m,
        text: m.text + delta,
        streaming: true,
      }));
    }

    case 'TEXT_MESSAGE_END':
      return mapMessage(state, (event as TextMessageEndEvent).messageId, (m) => ({
        ...m,
        streaming: false,
      }));

    case 'TOOL_CALL_START': {
      const { toolCallId, toolCallName, parentMessageId } = event as ToolCallStartEvent;
      const call: ChatToolCall = {
        id: toolCallId,
        name: toolCallName,
        arguments: '',
        finished: false,
        runId: state.currentRunId,
        parentMessageId,
        status: 'streaming',
      };
      const next = messageForToolCall(state, parentMessageId);
      const targetId = parentMessageId ?? next.messages[next.messages.length - 1]?.id;
      if (!targetId) return state;
      return mapMessage(next, targetId, (m) => ({
        ...m,
        toolCalls: [...m.toolCalls, call],
      }));
    }

    case 'TOOL_CALL_ARGS': {
      const { toolCallId, delta } = event as ToolCallArgsEvent;
      if (!delta) return state;
      return mapToolCall(state, toolCallId, (c) => ({
        ...c,
        arguments: c.arguments + delta,
      }));
    }

    case 'TOOL_CALL_END':
      return mapToolCall(state, (event as ToolCallEndEvent).toolCallId, (c) => ({
        ...c,
        finished: true,
      }));

    case 'TOOL_CALL_RESULT': {
      const { toolCallId, content } = event as ToolCallResultEvent;
      return mapToolCall(state, toolCallId, (c) => ({
        ...c,
        finished: true,
        result: content,
        status: 'done',
      }));
    }

    case 'RUN_FINISHED': {
      const traceId = (event as RunFinishedEvent).result?.traceId;
      return {
        ...state,
        running: false,
        traceId,
        messages: state.messages.map((m) =>
          m.role === 'assistant'
            ? { ...m, streaming: false, traceId: m.traceId ?? traceId }
            : m
        ),
      };
    }

    case 'RUN_ERROR':
      return {
        ...state,
        running: false,
        error: (event as RunErrorEvent).message,
        messages: state.messages.map((m) =>
          m.streaming ? { ...m, streaming: false } : m
        ),
      };

    default:
      return state;
  }
}

/** Marks a run as failed without a RUN_ERROR frame (transport-level failure). */
export function failRun(state: RunState, message: string): RunState {
  return {
    ...state,
    running: false,
    error: message,
    messages: state.messages.map((m) => (m.streaming ? { ...m, streaming: false } : m)),
  };
}

/**
 * Calls of `runId` the CLIENT still has to execute: arguments complete, no
 * result, and a registered client tool name. A server tool always answers with
 * TOOL_CALL_RESULT, so it never appears here.
 */
export function pendingClientToolCalls(
  state: RunState,
  runId: string | undefined,
  clientToolNames: ReadonlySet<string>
): ChatToolCall[] {
  if (!runId) return [];
  return state.messages
    .flatMap((m) => m.toolCalls)
    .filter(
      (c) =>
        c.runId === runId &&
        c.finished &&
        c.result === undefined &&
        c.status === 'streaming' &&
        clientToolNames.has(c.name)
    );
}

export function setToolCallStatus(
  state: RunState,
  toolCallId: string,
  status: ToolCallStatus,
  result?: string
): RunState {
  return mapToolCall(state, toolCallId, (c) => ({
    ...c,
    status,
    result: result ?? c.result,
  }));
}

/** Replaces the follow-up prompts of the newest assistant message. */
export function setFollowups(state: RunState, followups: string[]): RunState {
  for (let i = state.messages.length - 1; i >= 0; i--) {
    if (state.messages[i].role !== 'assistant') continue;
    const messages = [...state.messages];
    messages[i] = { ...messages[i], followups };
    return { ...state, messages };
  }
  return state;
}

function emptyMessage(id: string): ChatMessage {
  return { id, role: 'assistant', text: '', streaming: true, toolCalls: [] };
}

function withMessage(state: RunState, id: string): RunState {
  if (state.messages.some((m) => m.id === id)) return state;
  return { ...state, messages: [...state.messages, emptyMessage(id)] };
}

/**
 * The assistant message a tool call belongs to. The server's `parentMessageId`
 * wins over the local `pending-` placeholder: the continuation run has to echo
 * that exact id or Mastra stores the tool call a second time.
 */
function messageForToolCall(state: RunState, parentMessageId?: string): RunState {
  if (!parentMessageId) return ensureOpenMessage(state);
  if (state.messages.some((m) => m.id === parentMessageId)) return state;
  const last = state.messages[state.messages.length - 1];
  if (
    last &&
    last.role === 'assistant' &&
    last.streaming &&
    last.id.startsWith(PENDING_ID_PREFIX)
  )
    return mapLastMessage(state, (m) => ({ ...m, id: parentMessageId }));
  return { ...state, messages: [...state.messages, emptyMessage(parentMessageId)] };
}

/** The last open assistant message, else a new one with a placeholder id. */
function ensureOpenMessage(state: RunState): RunState {
  const last = state.messages[state.messages.length - 1];
  if (last && last.role === 'assistant' && last.streaming) return state;
  return {
    ...state,
    messages: [...state.messages, emptyMessage(`${PENDING_ID_PREFIX}${Date.now()}`)],
  };
}

function mapMessage(
  state: RunState,
  id: string,
  fn: (message: ChatMessage) => ChatMessage
): RunState {
  let changed = false;
  const messages = state.messages.map((m) => {
    if (m.id !== id) return m;
    changed = true;
    return fn(m);
  });
  return changed ? { ...state, messages } : state;
}

function mapLastMessage(
  state: RunState,
  fn: (message: ChatMessage) => ChatMessage
): RunState {
  if (state.messages.length === 0) return state;
  const messages = [...state.messages];
  messages[messages.length - 1] = fn(messages[messages.length - 1]);
  return { ...state, messages };
}

function mapToolCall(
  state: RunState,
  toolCallId: string,
  fn: (call: ChatToolCall) => ChatToolCall
): RunState {
  let changed = false;
  const messages = state.messages.map((m) => {
    if (!m.toolCalls.some((c) => c.id === toolCallId)) return m;
    changed = true;
    return { ...m, toolCalls: m.toolCalls.map((c) => (c.id === toolCallId ? fn(c) : c)) };
  });
  return changed ? { ...state, messages } : state;
}
